import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import PageHeader from '../components/PageHeader';
import Alert from '../components/Alert';
import { supabase, isSupabaseConfigured } from '../supabaseClient';

const SERVICE_OPTS = [
'Kitchen Remodeling',
'Bathroom Renovation',
'Flooring & Tile',
'Interior or Exterior Painting',
'Window & Door Replacement',
'Deck or Patio',
'General Home Renovation',
'Multiple Services / Whole-Home Remodel',
'Other / Not Sure Yet',
];

const initForm = {
firstName: '',
lastName: '',
email: '',
phone: '',
service: '',
city: '',
message: '',
termsAccepted: false,
marketingConsent: false,
};

function Contact() {
const [form, setForm] = useState(initForm);
const [submitting, setSubmitting] = useState(false);
const [success, setSuccess] = useState(false);
const [error, setError] = useState('');

const handleChange = e => {
const { name, value, type, checked } = e.target;
setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
};

const validatePhone = phone => /^[\d\s\-()+.]{7,20}$/.test(phone);
const validateEmail = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const handleSubmit = async e => {
e.preventDefault();
if (!form.firstName.trim() || !form.lastName.trim()) return setError('Please enter your full name.');
if (!validateEmail(form.email)) return setError('Please enter a valid email address.');
if (!validatePhone(form.phone)) return setError('Please enter a valid phone number.');
if (!form.service) return setError('Please select a service.');
if (!form.termsAccepted) return setError('Please agree to the Terms & Conditions and Privacy Policy.');

setError('');
setSubmitting(true);

try {
if (isSupabaseConfigured) {
const { error: dbErr } = await supabase.from('az_home_leads').insert([{
first_name: form.firstName.trim(),
last_name: form.lastName.trim(),
email: form.email.trim().toLowerCase(),
phone: form.phone.trim(),
service_type: form.service,
city: form.city.trim(),
message: form.message.trim(),
sms_consent: form.termsAccepted,
source: 'contact_form',
created_at: new Date().toISOString(),
}]);
if (dbErr) throw dbErr;

if (form.marketingConsent) {
await supabase.from('sms_consents').insert([{
first_name: form.firstName.trim(),
last_name: form.lastName.trim(),
email: form.email.trim().toLowerCase(),
phone: form.phone.trim(),
consent_given: true,
consent_type: 'marketing',
source: 'contact_form',
ip_address: null,
created_at: new Date().toISOString(),
}]);
}
}

setSuccess(true);
setForm(initForm);
} catch (err) {
console.error(err);
setError('We encountered an error. Please call us at (602) 390-2536 or email info@azhomefundingservices.com.');
} finally {
setSubmitting(false);
}
};

return (
<>
<Helmet>
<title>Contact Us | A-Z Home Improvement LLC | Phoenix, AZ</title>
<meta name="description" content="Contact A-Z Home Improvement LLC for a free in-home estimate. Serving Phoenix, Scottsdale, Tempe, Mesa, Chandler, and the greater Phoenix metro area." />
</Helmet>
<PageHeader
title="Contact Us"
subtitle="Request a free in-home estimate or ask us about your project. We respond to all inquiries within one business day."
/>

<section className="section">
<div className="container two-col" style={{ gap: 40, alignItems: 'flex-start' }}>
{/* Form */}
<div style={{ flex: 2 }}>
<h2 style={{ marginTop: 0 }}>Request a Free Estimate</h2>
<p className="helper" style={{ marginBottom: 22 }}>Fill out the form below and a member of our team will reach out within 24 hours to schedule your free in-home consultation.</p>

{success && (
<Alert type="success">
<strong>Thank you! Your request has been received.</strong> A member of our team will contact you within one business day to schedule your free in-home estimate. You will receive an SMS confirmation shortly. Reply STOP at any time to opt out.
</Alert>
)}
{error && <Alert type="error">{error}</Alert>}

<form className="form" onSubmit={handleSubmit} noValidate>
<div className="form-grid">
<div className="field">
<label htmlFor="firstName">First Name <span aria-hidden="true">*</span></label>
<input id="firstName" name="firstName" type="text" value={form.firstName} onChange={handleChange} placeholder="Robert" autoComplete="given-name" required />
</div>
<div className="field">
<label htmlFor="lastName">Last Name <span aria-hidden="true">*</span></label>
<input id="lastName" name="lastName" type="text" value={form.lastName} onChange={handleChange} placeholder="Smith" autoComplete="family-name" required />
</div>
</div>
<div className="form-grid">
<div className="field">
<label htmlFor="email">Email Address <span aria-hidden="true">*</span></label>
<input id="email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="you@example.com" autoComplete="email" required />
</div>
<div className="field">
<label htmlFor="phone">Phone Number <span aria-hidden="true">*</span></label>
<input id="phone" name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="(602) 555-1234" autoComplete="tel" required />
</div>
</div>
<div className="field">
<label htmlFor="service">Type of Project <span aria-hidden="true">*</span></label>
<select id="service" name="service" value={form.service} onChange={handleChange} required>
<option value="">-- Select a project type --</option>
{SERVICE_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
</select>
</div>
<div className="field">
<label htmlFor="city">Your City</label>
<input id="city" name="city" type="text" value={form.city} onChange={handleChange} placeholder="Phoenix, Scottsdale, Mesa..." autoComplete="address-level2" />
</div>
<div className="field">
<label htmlFor="message">Project Details</label>
<textarea id="message" name="message" rows={4} value={form.message} onChange={handleChange} placeholder="Tell us about your project — what you'd like done, any ideas you have, timeline, or questions..." />
</div>

<div className="consentBox">
<p style={{ fontWeight: 700, margin: '0 0 10px' }}>Terms &amp; Marketing Consent</p>
<div className="consent-grid">
<div className="consent-row">
<input type="checkbox" id="termsAccepted" name="termsAccepted" checked={form.termsAccepted} onChange={handleChange} style={{ marginTop: 2 }} />
<label htmlFor="termsAccepted" style={{ cursor: 'pointer' }}>
<strong>I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand)', fontWeight: 600 }}>Terms &amp; Conditions</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand)', fontWeight: 600 }}>Privacy Policy</a>.</strong>
</label>
</div>
<div className="consent-row" style={{ marginTop: 14 }}>
<input type="checkbox" id="marketingConsent" name="marketingConsent" checked={form.marketingConsent} onChange={handleChange} style={{ marginTop: 2 }} />
<label htmlFor="marketingConsent" style={{ cursor: 'pointer' }}>
<strong>(Optional) I agree to receive SMS marketing messages from A-Z Home Improvement LLC about home improvement services, promotional offers, and updates. Message frequency may vary. Message &amp; data rates may apply. Reply STOP to opt out or HELP for help. Consent is not required to purchase any service.</strong>
</label>
</div>
</div>
<p style={{ margin: '10px 0 0', fontSize: 12, color: '#666' }}>
<strong>To opt out:</strong> Reply STOP to any message.<br />
<strong>For help:</strong> Reply HELP or email <a href="mailto:support@azhomefundingservices.com" style={{ color: 'var(--brand)' }}>support@azhomefundingservices.com</a>
</p>
</div>

<button type="submit" className="btn" style={{ fontSize: 15, padding: '12px 28px', marginTop: 4 }} disabled={submitting}>
{submitting ? 'Sending…' : 'Send My Request'}
</button>
</form>
</div>

{/* Contact info sidebar */}
<div style={{ flex: 1, minWidth: 220 }}>
<div className="card" style={{ padding: 22 }}>
<h3 style={{ marginTop: 0 }}>Contact Information</h3>
<div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
<div>
<p className="helper" style={{ margin: '0 0 2px', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Phone</p>
<a href="tel:+16023902536" style={{ fontWeight: 700, color: 'var(--brand)', textDecoration: 'none', fontSize: 16 }}>(602) 390-2536</a>
<p className="helper" style={{ margin: '2px 0 0', fontSize: 12 }}>Mon–Fri 7am–6pm · Sat 8am–3pm</p>
</div>
<div>
<p className="helper" style={{ margin: '0 0 2px', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Email</p>
<a href="mailto:info@azhomefundingservices.com" style={{ fontWeight: 600, color: 'var(--brand)', textDecoration: 'none', fontSize: 14 }}>info@azhomefundingservices.com</a>
</div>
<div>
<p className="helper" style={{ margin: '0 0 2px', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Office Address</p>
<address style={{ fontStyle: 'normal', fontSize: 14, lineHeight: 1.6 }}>
3842 W. McDowell Rd, Suite 110<br />
Phoenix, AZ 85009
</address>
</div>
<div>
<p className="helper" style={{ margin: '0 0 2px', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>License</p>
<p style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>AZ ROC #XXXXX8142</p>
</div>
</div>
</div>

<div className="card" style={{ padding: 20, marginTop: 16 }}>
<h3 style={{ marginTop: 0, fontSize: 16 }}>Service Areas</h3>
<p className="helper" style={{ margin: '0 0 10px', fontSize: 13 }}>We serve the greater Phoenix metro area, including:</p>
<div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
{['Phoenix', 'Scottsdale', 'Tempe', 'Mesa', 'Chandler', 'Gilbert', 'Glendale', 'Peoria', 'Surprise', 'Goodyear'].map(c => (
<span key={c} className="badge" style={{ fontSize: 12 }}>{c}</span>
))}
</div>
</div>
</div>
</div>
</section>
</>
);
}

export default Contact;
